import type { ReactNode } from 'react'

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

export interface ListTab<K extends string = string> {
  key: K
  label: ReactNode
  /** Rendered before the label — a lucide icon. */
  icon?: ReactNode
  /** Row count, shown greyed after the label. Hidden when undefined. */
  count?: number
}

export interface ListTabsProps<K extends string = string> {
  tabs: ListTab<K>[]
  value: K
  onValueChange: (key: K) => void
  /**
   * `segmented` (default) is the pill strip used in card headers; `line` is a
   * full-width strip of plain labels with a blue underline under the active one,
   * for switching between whole sections of a page.
   */
  variant?: 'segmented' | 'line'
  className?: string
}

/**
 * A segmented tab strip for switching what a card shows, on the shadcn/Radix
 * {@link Tabs} primitive so keyboard and ARIA behaviour come from Radix.
 *
 * Unlike {@link RouteTabs} it owns nothing but the buttons: the panels are the
 * caller's own markup, and the active tab lives in the caller's state. Use it
 * wherever a card header switches between lists, or for a plain two-option
 * toggle.
 */
export function ListTabs<K extends string = string>({
  tabs,
  value,
  onValueChange,
  variant = 'segmented',
  className,
}: ListTabsProps<K>) {
  const line = variant === 'line'
  return (
    <Tabs
      value={value}
      onValueChange={(v) => onValueChange(v as K)}
      className={cn(line ? 'w-full gap-0' : 'w-fit shrink-0 gap-0', className)}
    >
      <TabsList
        variant={line ? 'line' : 'default'}
        className={cn(
          line &&
            'w-full justify-start gap-0 border-b border-(--ad-border) p-0 group-data-[orientation=horizontal]/tabs:h-11'
        )}
      >
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.key}
            value={tab.key}
            className={cn(
              line &&
                // Full height, and the underline dropped by the list's 1px border,
                // so the active line sits over the grey one rather than above it.
                'h-full flex-none gap-2 px-4 data-[state=active]:text-primary dark:data-[state=active]:text-primary after:bg-primary group-data-[orientation=horizontal]/tabs:after:bottom-[-1px] group-data-[orientation=horizontal]/tabs:after:h-0.75'
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && (
              <span className="text-xs text-(--ad-text-muted)">({tab.count})</span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
    </Tabs>
  )
}
